import { createSelector } from 'reselect';
import _ from 'lodash';

import { campaignPromotionListSortBy, campaignPromotionSort, sortDirections } from './promotion-enums';

const getAccountApiKey = () => App.globalContext.account.apikey;

const _scopedState = (state) => state.entities.promotions;

const campaignPromotionsById = (state) => _scopedState(state).byId;
const isFetching = (state) => _scopedState(state).isFetching;
const isEditing = (state) => _scopedState(state).isEditing;
const sortListBy = (state) => _scopedState(state).sortListBy;
const confirmDeleteVisible = (state) => _scopedState(state).confirmDeleteVisible;

const promotionsSnapshot = (state) => _scopedState(state).promotionsSnapshot;
const activePromotion = (state) => _scopedState(state).activePromotion;

const successMessage = (state) => _scopedState(state).successMessage;

// grab the promotions by id and spit them back out as an array
const _campaignPromotionList = createSelector([campaignPromotionsById], (promotions) => _.values(promotions));

const campaignPromotionById = createSelector(
  [campaignPromotionsById, (state, id) => id],
  (promotions, id) => promotions[id]
);
const isEmpty = createSelector(campaignPromotionsById, (p) => Object.keys(p).length === 0);

// ######################################################################################## //
// FILTERING PROMOTION SELECTOR
// ######################################################################################## //

// arrange the list of promotions into an object representing each state with each key storing an array of promotions
const promotionsByActiveState = createSelector([_campaignPromotionList], (promotions) => {
  return _.reduce(
    promotions,
    (acc, p) => {
      switch (true) {
        case p.isActiveOpen:
          acc.activeOpen.push(p);
          break;
        case p.isActive:
          acc.active.push(p);
          break;
        case p.isFuture:
          acc.future.push(p);
          break;
        case p.isPast:
          acc.past.push(p);
          break;
        case p.isInvalid:
          acc.invalid.push(p);
          break;
      }
      return acc;
    },
    {
      activeOpen: [],
      active:     [],
      future:     [],
      past:       [],
      invalid:    [],
    }
  );
});

// ######################################################################################## //
// SORTING PROMOTION SELECTOR
// ######################################################################################## //

/**
 * compare two passed in values. if they are the same, return 0, otherwise use the passed in direction to work out which way to compare
 * @param a {string} first comparator
 * @param b {string} second comparator
 * @param dir {string} sort direction
 * @returns {number}
 */
const compare = (a, b, dir) => (a === b ? 0 : dir === sortDirections.ASCENDING ? (a < b ? -1 : 1) : a > b ? -1 : 1);

/**
 * pass a list of promotions and an array of fields to sort by in ascending order
 * if the first field matches, it will drop through to the next field and so on
 * @param promotions {array} array of unsorted promotions
 * @param sortFields {array} array of string field names to sort by
 * @param dir {string} sort direction
 * @returns {array} array of sorted promotions
 */

const sortPromotionsByFields = (promotions = [], sortFields, dir = sortDirections.ASCENDING) =>
  promotions.sort((a, b) => {
    sortFields.map((s) => compare(a[s], b[s], dir));
  });

// promotions sorted by "activeOpen" (dateCreated, id)
const _sortPromotionsByActiveOpen = createSelector([promotionsByActiveState], (promotions) =>
  sortPromotionsByFields(promotions.activeOpen, campaignPromotionSort.ACTIVE_OPEN)
);

// promotions sorted by "active" (validFrom, dateCreated, id)
const _sortPromotionsByActive = createSelector([promotionsByActiveState], (promotions) =>
  sortPromotionsByFields(promotions.active, campaignPromotionSort.ACTIVE)
);

// promotions sorted by "future" (validFrom, dateCreated, id)
const _sortPromotionsByFuture = createSelector([promotionsByActiveState], (promotions) =>
  sortPromotionsByFields(promotions.future, campaignPromotionSort.FUTURE)
);

// promotions sorted by "past" (validTo, dateCreated, id)
const _sortPromotionsByPast = createSelector([promotionsByActiveState], (promotions) =>
  sortPromotionsByFields(promotions.past, campaignPromotionSort.PAST)
);

// invalid promotions sorted by date created descending
const _sortPromotionsByInvalid = createSelector([promotionsByActiveState], (promotions) =>
  sortPromotionsByFields(promotions.invalid, campaignPromotionSort.DATE_CREATED, sortDirections.DESCENDING)
);

// promotions sorted by date created descending
const _sortPromotionsByDateCreated = createSelector([_campaignPromotionList], (promotions) =>
  _.orderBy(promotions, campaignPromotionSort.DATE_CREATED, sortDirections.DESCENDING)
);

// promotions sorted by score then date created
const _sortPromotionsByScore = createSelector([_campaignPromotionList], (promotions) => {
  return _.orderBy(promotions, campaignPromotionSort.SCORE);
});

// ######################################################################################## //
// SORT PROMOTION SELECTORS
// ######################################################################################## //
const sortedPromotions = createSelector(
  [
    sortListBy,
    _campaignPromotionList,
    _sortPromotionsByActiveOpen,
    _sortPromotionsByActive,
    _sortPromotionsByFuture,
    _sortPromotionsByPast,
    _sortPromotionsByInvalid,
    _sortPromotionsByDateCreated,
    _sortPromotionsByScore,
  ],
  (
    sortListBy,
    promotions,
    activeOpenPromotions,
    activePromotions,
    futurePromotions,
    pastPromotions,
    invalidPromotions,
    promotionsByDateCreated,
    promotionsByScore
  ) => {
    switch (sortListBy) {
      case campaignPromotionListSortBy.CURRENT_FUTURE_PAST:
        return [
          ...activePromotions,
          ...activeOpenPromotions,
          ...futurePromotions,
          ...pastPromotions,
          ...invalidPromotions,
        ];

      case campaignPromotionListSortBy.PAST_CURRENT_FUTURE:
        return [
          ...pastPromotions,
          ...activePromotions,
          ...activeOpenPromotions,
          ...futurePromotions,
          ...invalidPromotions,
        ];

      case campaignPromotionListSortBy.DATE_CREATED:
        return promotionsByDateCreated;

      case campaignPromotionListSortBy.MANUAL:
        return promotionsByScore;

      case campaignPromotionListSortBy.RANDOM:
        return _.shuffle(promotions);

      default:
        return promotionsByScore;
    }
  }
);

/**
 * This selector is used to get the unsaved promotions.
 * It will return an array of promotions that have been modified but not saved, including the active promotion if it has been modified.
 */
const getUnsavedPromotions = createSelector(
  [campaignPromotionsById, promotionsSnapshot, activePromotion],
  (promotionsById, promotionsSnapshot, activePromotion) => {
    //make sure the active promotion is saved into the promotions list
    const finalPromotionsById = {
      ...promotionsById,
      ...(activePromotion ? { [activePromotion.id]: activePromotion } : {}),
    };
    return _.filter(finalPromotionsById, (promotion) => !_.isEqual(promotion, promotionsSnapshot[promotion.id]));
  }
);

/**
 * This selector is used to check if the rewards are dirty(changed but not saved).
 * It will return true if there are any unsaved rewards, otherwise it will return false.
 */
const arePromotionsDirty = createSelector([getUnsavedPromotions], (unsavedPromotions) => unsavedPromotions.length > 0);

const isDiscardChangesModalVisible = (state) => _scopedState(state).isDiscardChangesModalVisible;

const isCloneModalVisible = (state) => _scopedState(state).isCloneModalVisible;

const isSaving = (state) => _scopedState(state).isSaving;

const selectedPromotionToClone = (state) => _scopedState(state).selectedPromotionToClone;

const activeEditPanel = (state) => _scopedState(state).activeEditPanel;

/**
 * This selector is used to get the preview data for rewards.
 * It will return an array of objects that will be used to populate the preview iframe.
 */
const getPreviewData = createSelector([activePromotion, getAccountApiKey], (activePromotion, apikey) => [
  {
    key:   'apikey',
    value: apikey,
  },
  {
    key:   'activeRewardId',
    value: activePromotion.id,
  },
  {
    key:   'promotions',
    value: JSON.stringify([activePromotion]),
  },
]);

export default {
  confirmDeleteVisible,
  isEmpty,
  isFetching,
  isEditing,
  isSaving,
  campaignPromotionById,
  sortListBy,
  sortedPromotions,
  getUnsavedPromotions,
  activePromotion,
  campaignPromotionsById,
  isDiscardChangesModalVisible,
  isCloneModalVisible,
  getPreviewData,
  selectedPromotionToClone,
  activeEditPanel,
  arePromotionsDirty,
  successMessage,
};
